import { useSyncExternalStore } from 'react';
import {
  DEFAULT_SETTINGS,
  normalizePlaybackPreferences,
  normalizeSettings,
  reuseUnchangedSettings,
  type VisiousSettings,
} from '@visious/core';
import { applyUiLocale } from '../strings';
import { readContentRegion, writeContentRegion } from './contentRegion';
import { readPlayerPrefs, writePlayerPrefs } from './playerPrefs';
import { writeSyncStamp } from './syncStamp';
import { applyThemeChoice, readThemeChoice } from './theme';

/**
 * §8's Settings, as one observable document — and it **composes** the three stores that already own
 * pieces of it rather than replacing them.
 *
 * That is the whole design decision in this file. `theme.ts`, `contentRegion.ts` and `playerPrefs.ts`
 * each existed before this slice, each is read on a path that must not wait for anything (the theme is
 * stamped before the first paint; the region is read while building a feed URL; the volume is read by
 * the player's mount effect), and each has a comment explaining its own key. Copying their values into
 * a fourth store would give every one of them two sources of truth — and the failure mode is the worst
 * kind: the settings page would show the value it wrote while the player kept using the value it read.
 *
 * So the shape is: **one type, four keys, one dispatcher.**
 *
 * | field | key | owner |
 * |---|---|---|
 * | `theme` | `visious.theme` | `theme.ts` — also stamps `<html data-theme>` |
 * | `contentRegion` | `visious.region` | `contentRegion.ts` — goes through core's allowlist |
 * | `volume`, `muted`, `playback.playbackRate` | `visious.player` | `playerPrefs.ts` |
 * | everything else | `visious.settings` | here |
 *
 * `visious.player` holding *part* of `playback` looks odd until you read §7's volume row: the volume
 * pair is written from user actions on the player and must never be written from the status poll, so it
 * is deliberately not in a store that a settings page writes wholesale.
 *
 * **Not** under the `visious.token.` prefix, and that is a rule with teeth: R9's
 * `clearOtherInstances()` deletes every key with that prefix that is not the current instance's, on
 * every boot (docs/specs/auth.md).
 *
 * These settings are per **page origin**, like every other stored preference, and deliberately *not*
 * per instance: what they hold is instance-portable, exactly as `channelMetaStore.ts` and
 * `videoMetaStore.ts` argue for their caches. Switching instances keeps your quality and your theme,
 * and loses your credential — which is the honest split.
 */

const KEY = 'visious.settings';

/** The keys a `storage` event from another tab can be about. */
const WATCHED_KEYS: readonly string[] = [KEY, 'visious.theme', 'visious.region', 'visious.player'];

/**
 * The blob under `visious.settings`: everything the table above does **not** delegate, plus the sync
 * stamp. It is `unknown` per field because it is storage — core's normalisers are what make it a
 * document (R4).
 *
 * **Deliberately not enumerated as a filter.** `readSettings` spreads this whole object into
 * `normalizeSettings` and `writeSettings` spreads the whole document back out minus the four delegated
 * fields, so adding a setting to `VisiousSettings` needs no edit here. 4b learned that the hard way: it
 * added `resumePlayback` to the document, to the normaliser, to the sync mapping and to the settings
 * page, and the field list in this file was the one place it did not add it — so the toggle wrote
 * nothing, read back its default, and looked like it was not wired to anything at all. Every gate
 * passed, because a missing key is not a type error anywhere.
 */
interface StoredSettings {
  readonly [field: string]: unknown;
}

/**
 * The sync stamp used to be a field in here, and `writeStored` drops it on the way past.
 *
 * It has its own key since 2026-09-08 (`syncStamp.ts` says why), so what is left is a leftover in
 * every browser that ran an earlier build. Stripping it on the next write rather than in a boot
 * migration costs nothing until something is written and needs no read path of its own —
 * `normalizeSettings` ignores a key it does not know, so the leftover was inert either way.
 */
const LEGACY_SYNC_STAMP_FIELD = 'lastSyncedEpochSec';

function readStored(): StoredSettings {
  try {
    const text = globalThis.localStorage.getItem(KEY);
    if (text === null) return {};
    const raw = JSON.parse(text) as unknown;
    return typeof raw === 'object' && raw !== null ? (raw as StoredSettings) : {};
  } catch {
    // Blocked storage, or a half-written value. Either way: defaults, not a boot failure.
    return {};
  }
}

function writeStored(next: StoredSettings): void {
  // `delete` on a copy, the shape `writeSettings` already uses for the delegated fields.
  const document: Record<string, unknown> = { ...next };
  delete document[LEGACY_SYNC_STAMP_FIELD];
  try {
    globalThis.localStorage.setItem(KEY, JSON.stringify(document));
  } catch {
    // A setting that cannot be saved still applies to this page load.
  }
}

/**
 * The deployment's defaults for the Visious-specific settings (`defaultSettingsFromConfig`), sitting
 * *under* whatever this browser has stored. `DEFAULT_SETTINGS` until the platform boot has read
 * `config.json` — which arrives a tick late, exactly like the instance URL itself, so a configured
 * default can appear one render in. Once anything is written, `writeSettings` stores the whole
 * document and the values in force are pinned; a later edit to the file only reaches fresh browsers.
 */
let configDefaults: VisiousSettings = DEFAULT_SETTINGS;

/** Called by the platform boot once `config.json` has been read (`instance.ts`). */
export function setConfigDefaults(defaults: VisiousSettings): void {
  configDefaults = defaults;
  publish();
}

/**
 * The whole document, assembled from the four stores. Total (R4): every value goes through core's
 * normalisers, so a stale build's shape or a hand-edited key cannot reach the player or the wire.
 */
export function readSettings(): VisiousSettings {
  const stored = readStored();
  const player = readPlayerPrefs();
  return normalizeSettings({
    // The deployment's defaults first, then everything this store owns, unfiltered —
    // `normalizeSettings` reads only the keys it knows and is total over every value, so spreading is
    // safe and cannot omit a field (see {@link StoredSettings}).
    ...configDefaults,
    ...stored,
    // Then the four the table above delegates, which override whatever the blob happens to hold.
    theme: readThemeChoice(),
    contentRegion: readContentRegion(),
    playback: {
      // The three track preferences from this store, the rate from the player's own. The config
      // default only speaks while nothing was ever stored — `writeSettings` stores `playback` whole.
      ...normalizePlaybackPreferences(stored['playback'] ?? configDefaults.playback),
      playbackRate: player.playbackRate,
    },
    volume: player.volume,
    muted: player.muted,
  });
}

// ---------------------------------------------------------------------------
// The store — one snapshot, because three places read it and they must not disagree
// ---------------------------------------------------------------------------

let snapshot: VisiousSettings | null = null;
const listeners = new Set<() => void>();

/**
 * Fires after {@link writeSettings} — a write in **this tab**, which is the sync module's cue to push
 * (`settingsSync.ts`). Deliberately narrower than the store's own `subscribe`: a `storage` event is
 * another tab's write, and that tab pushes for itself, and `setConfigDefaults` changes nothing the
 * instance stores.
 */
const writeListeners = new Set<(next: VisiousSettings) => void>();

export function onSettingsWritten(listener: (next: VisiousSettings) => void): () => void {
  writeListeners.add(listener);
  return () => writeListeners.delete(listener);
}

function publish(): void {
  const next = reuseUnchangedSettings(readSettings(), snapshot);
  /*
   * Identity is the whole test. A re-read that produced an equal document has nothing to tell anyone,
   * and the case that lands here is the common one: a signed-in boot in another tab, which applies
   * the account's preferences over a cache that already matched them and writes the same bytes back.
   * `setItem` fires a `storage` event either way. Publishing it re-rendered every subscriber for no
   * change, and a grid subscribes once per card.
   */
  if (next === snapshot) return;
  snapshot = next;
  for (const listener of listeners) listener();
}

function getSnapshot(): VisiousSettings {
  snapshot ??= readSettings();
  return snapshot;
}

/**
 * The fields the three older stores own, so this one must not keep a copy — the table at the top of the
 * file, as data. Typed against `keyof VisiousSettings`, so renaming a field breaks the build here rather
 * than leaving a stale string that silently starts storing a duplicate.
 */
const DELEGATED_FIELDS = [
  'theme',
  'contentRegion',
  'volume',
  'muted',
] as const satisfies readonly (keyof VisiousSettings)[];

/** The three track preferences, with the rate left to `visious.player` — see {@link writeSettings}. */
function withoutRate(playback: VisiousSettings['playback']): Omit<VisiousSettings['playback'], 'playbackRate'> {
  return { quality: playback.quality, audio: playback.audio, subtitle: playback.subtitle };
}

/**
 * Apply a patch and tell every reader. Returns the settings **as they now are**, which is what a
 * caller pushing to the instance needs: a patch plus the previous snapshot is not the same thing as
 * the merged result once a normaliser has had its say (`volume: 5` is stored as `1`).
 */
export function writeSettings(patch: Partial<VisiousSettings>): VisiousSettings {
  const current = getSnapshot();

  // `applyThemeChoice`, not `writeThemeChoice`: storing a theme without stamping `<html data-theme>`
  // leaves the page in the old one until a reload, with the control already showing the new one.
  if (patch.theme !== undefined) applyThemeChoice(patch.theme);
  // 4e, and the same rule one line up states: storing a language is not speaking it. Applied here as
  // well as at the settings page's own setter, so a write from anywhere — a pull from the instance,
  // another tab — moves the catalogue rather than leaving the page in the previous language.
  if (patch.uiLocale !== undefined) applyUiLocale(patch.uiLocale);
  if (patch.contentRegion !== undefined) writeContentRegion(patch.contentRegion);

  if (patch.volume !== undefined || patch.muted !== undefined || patch.playback !== undefined) {
    const player = readPlayerPrefs();
    writePlayerPrefs({
      volume: patch.volume ?? player.volume,
      muted: patch.muted ?? player.muted,
      playbackRate: patch.playback?.playbackRate ?? player.playbackRate,
    });
  }

  /*
   * The merged document minus the delegated fields, written wholesale rather than field by field.
   *
   * The four names below are the *only* thing this function knows about `VisiousSettings`' shape, and
   * that is the point (see {@link StoredSettings}): a field added to the document is stored without an
   * edit here, where an enumerated list silently drops it. `theme`, `contentRegion`, `volume` and
   * `muted` are destructured out because they belong to the three stores above.
   *
   * `playback` is written **without the rate**, which is the table taken literally: the rate lives in
   * `visious.player`, and a copy here would be a second source of truth. A browser found the
   * consequence — a hand-written `visious.settings` with `playbackRate: 1.25` beside a `visious.player`
   * at 1 plays at 1, so the copy is not merely redundant, it is *wrong* and looks authoritative.
   */
  const merged: VisiousSettings = { ...current, ...patch };
  const ownFields: Record<string, unknown> = { ...merged, playback: withoutRate(merged.playback) };
  for (const field of DELEGATED_FIELDS) delete ownFields[field];
  writeStored({ ...readStored(), ...ownFields });

  publish();
  const next = getSnapshot();
  for (const listener of writeListeners) listener(next);
  return next;
}

/**
 * One `storage` listener for the whole document, installed on the first subscriber and never taken
 * down.
 *
 * It used to be registered inside {@link subscribe}, which made it one listener per *subscriber* —
 * and `useSettings` is reached once per card through `useDeArrowBranding`, so a scrolled grid carried
 * a hundred of them. A single `storage` event then ran {@link publish} a hundred times, each re-reading
 * four `localStorage` keys and notifying all hundred listeners: quadratic work with a synchronous
 * storage read per step, which froze the tab for seconds every time another tab wrote a setting. A
 * signed-in boot does exactly that on every load (`settingsSync.ts` stamps its sync time), which is
 * why the freeze was signed-in only.
 *
 * Nothing is lost by never removing it: the handler is stateless, the store is a module that lives as
 * long as the document, and the guard makes a second install impossible.
 */
let watchingOtherTabs = false;

function watchOtherTabs(): void {
  if (watchingOtherTabs) return;
  watchingOtherTabs = true;
  /*
   * Another tab is a real case here for the same reason it is for the token (`session.ts`): these keys
   * are shared across every tab on this origin, so without this a second tab keeps playing at the old
   * default quality and showing the old theme until it is reloaded. `event.key === null` is a
   * `localStorage.clear()`, which has to count as "everything changed".
   */
  globalThis.addEventListener('storage', (event: StorageEvent) => {
    if (event.key === null || WATCHED_KEYS.includes(event.key)) publish();
  });
}

function subscribe(onChange: () => void): () => void {
  watchOtherTabs();
  listeners.add(onChange);
  return () => listeners.delete(onChange);
}

/** The settings, re-read whenever they change — in this tab or another one. */
export function useSettings(): VisiousSettings {
  return useSyncExternalStore(subscribe, getSnapshot, () => DEFAULT_SETTINGS);
}

/**
 * One branch of the settings document, subscribed to on its own.
 *
 * For the components there are *many* of. `useSettings` re-renders its caller whenever any part of
 * the document moves, which is right for a settings page and wrong for a grid card that reads one
 * switch: changing the theme re-rendered every card on screen, and there can be a hundred of them.
 * This hands back the branch itself, and {@link publish} keeps a branch at its previous identity
 * unless that branch changed (core's `reuseUnchangedSettings`), so React bails out of the render.
 *
 * `select` is called on every read, so it must be cheap and must not build anything: return a branch
 * or a field, never a new object — one assembled per call is a new identity every time and defeats
 * the whole arrangement.
 */
export function useSettingsSlice<T>(select: (settings: VisiousSettings) => T): T {
  return useSyncExternalStore(
    subscribe,
    () => select(getSnapshot()),
    () => select(DEFAULT_SETTINGS),
  );
}

/**
 * Drop everything this browser has stored about settings, back to the defaults.
 *
 * Its own function rather than a patch of defaults because it must also clear the sync stamp —
 * otherwise "reset" would leave a browser that still believes it agreed a sync with the instance, and
 * the very next change would push the defaults over the account's values.
 */
export function resetSettings(): VisiousSettings {
  // The stamp goes with them. It used to be inside this key and leave for free; now that it has one
  // of its own (`syncStamp.ts`), this line is what keeps that true.
  writeSyncStamp(null);
  try {
    globalThis.localStorage.removeItem(KEY);
  } catch {
    // Nothing to recover: the defaults still apply for this page load.
  }
  applyThemeChoice(DEFAULT_SETTINGS.theme);
  applyUiLocale(DEFAULT_SETTINGS.uiLocale);
  writeContentRegion(null);
  writePlayerPrefs({
    volume: DEFAULT_SETTINGS.volume,
    muted: DEFAULT_SETTINGS.muted,
    playbackRate: DEFAULT_SETTINGS.playback.playbackRate,
  });
  publish();
  return getSnapshot();
}

